import { EVA_API_VERSION } from '@springtree/eva-services-core';
import { evaServiceInterceptors } from './interceptors.js';
import { getServiceSettings, setServiceSetting } from './settings.js';
import { logger } from '@springtree/eva-sdk-core-logger';
import { getCoreSettings } from '@springtree/eva-sdk-core-settings';
import ky from 'ky';
import { v4 } from 'uuid';
import md5 from 'md5';
import { stringify } from 'safe-stable-stringify';
/**
 * Helper method to get the JSON body from a fetch response
 *
 * @param {Response} response
 * @returns {(Promise<any|undefined>)}
 */
export const safeGetResponseJSON = async (response) => {
    let responseBody;
    try {
        const textResponse = await response.text();
        if (textResponse?.length) {
            responseBody = JSON.parse(textResponse);
        }
    }
    catch (jsonError) {
        logger.warn('[EVA:SERVICE] Failed to extract JSON from response', jsonError)();
    }
    return responseBody;
};
/**
 * Helper method to create a stable md5 hash for a JSON object
 *
 * @param {Record<string|number, any>} jsonData
 * @returns {string}
 */
export const safeStableJSONHash = (jsonData = {}) => {
    try {
        const hashed = md5(stringify(jsonData));
        return hashed;
    }
    catch (jsonError) {
        logger.warn('[EVA:SERVICE] Failed to hash JSON', jsonError, jsonData)();
        return '';
    }
};
/**
 * The EVA Service implementation
 *
 * @export
 * @class EvaService
 * @template REQ
 * @template RES
 */
export class EvaService {
    data;
    endpoint;
    /**
     * The name of the EVA Service being called
     *
     * @private
     */
    serviceName = 'generic';
    /**
     * The current abort controller for the fetch request
     *
     * @private
     * @type {AbortController}
     */
    abortController;
    /**
     * The applied default options for the call
     *
     * @private
     * @type {Options}
     */
    defaultOptions;
    /**
     * Stops the current service call
     *
     */
    abort() {
        this.abortController && this.abortController.abort();
    }
    /**
     * The fetch response object
     *
     * @type {(Response|undefined)}
     */
    fetchResponse;
    /**
     * Creates an instance of EvaService
     * pre-configured with default EVA headers and options
     *
     * @param {SVC} data The service definition that will be used to make the call and receive the result
     * @param {EvaEndpoint} endpoint The bootstrapped EVA endpoint to use for the call
     * @param {IEvaServiceArguments} [args] Optional arguments for instantiating the service call
     */
    constructor(data, endpoint, args) {
        this.data = data;
        this.endpoint = endpoint;
        // Collect required settings
        //
        const coreSettings = getCoreSettings();
        const serviceSettings = getServiceSettings();
        if (coreSettings.appName === 'UNKNOWN') {
            throw new Error('Core setting missing: appName');
        }
        if (coreSettings.appVersion === '0.0.0') {
            throw new Error('Core setting missing: appVersion');
        }
        if (!this.data || !this.data.path) {
            throw new Error('Missing required EVA service url');
        }
        if (!this.endpoint || !this.endpoint.hasBootstrapped && (!args || !args.disableEndpointBootstrapCheck)) {
            throw new Error('Endpoint has not been bootstrapped');
        }
        // Extract the service name from the path
        //
        const urlSplit = `${this.data.path}`.split('/');
        const serviceName = urlSplit[urlSplit.length - 1];
        this.serviceName = serviceName;
        // Build the request options setting default headers
        // and parameters
        //
        this.defaultOptions = {
            fetch: serviceSettings.fetch,
            method: 'POST',
            timeout: serviceSettings.defaultTimeout,
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Accept-Language': coreSettings.language,
                'Content-type': 'application/json',
                ClientName: coreSettings.clientName,
                ClientVersion: coreSettings.clientVersion,
                'EVA-User-Agent': `${coreSettings.appName}/${coreSettings.appVersion}`,
                'EVA-API-Version': `${EVA_API_VERSION}`,
            },
            hooks: {
                beforeRequest: [
                    ...((!args || !args.disableInterceptors) ? evaServiceInterceptors.getRequestInterceptors() : []),
                ],
                afterResponse: [
                    (request, options, response) => {
                        const requestUrl = new URL(request.url);
                        const origin = requestUrl.origin;
                        const serviceSettings = getServiceSettings();
                        const applicationTokens = serviceSettings.applicationTokens || {};
                        // Check for EVA-Warning headers and log them to the console
                        // This used to be an interceptor/hook but is so important it is now part
                        // of the core service class
                        //
                        response.headers.forEach((value, name) => {
                            if (`${name}`.toLowerCase() === 'EVA-Warning'.toLowerCase()) {
                                // EVA Warnings should always be logged so we will not use the logger
                                // which can be disabled
                                //
                                console.warn(`[EVA:WarningLogger] ${value}`);
                            }
                            if (`${name}`.toLowerCase() === 'EVA-App-Token'.toLowerCase()) {
                                // EVA Application tokens are usually returned on create order
                                // calls to provide secure access to anonymous orders
                                //
                                console.log(`[EVA:AppToken] received new application token ${value} for origin ${origin}`);
                                applicationTokens[origin] = value;
                                setServiceSetting('applicationTokens', applicationTokens);
                            }
                        });
                        // NOOP
                    },
                    async (request, options, response) => {
                        // Check for the Error content to be present
                        // Error is the new property for this this used to be called Exception
                        //
                        if (response.status < 400) {
                            const responseBody = await safeGetResponseJSON(response);
                            if (responseBody && responseBody.Error) {
                                logger.warn(`[EVA:${serviceName}] Error found in response, rejecting service call`, responseBody.Error)();
                                const errorResponse = new Response(JSON.stringify(responseBody), {
                                    status: 500,
                                    headers: response.headers,
                                    statusText: 'Internal Server Error',
                                });
                                return errorResponse;
                            }
                            // For the login call there is another error scenario and that is when
                            // the `Authentication` value is not EVA.Core.AuthenticationResults.Authenticated (2)
                            //
                            if (responseBody && responseBody.Authentication !== undefined && responseBody.Authentication !== 2) {
                                logger.warn(`[EVA:${serviceName}] Failed authentication, rejecting service call`, responseBody)();
                                const authFailedResponse = new Response(JSON.stringify(responseBody), {
                                    status: 409,
                                    headers: response.headers,
                                    statusText: `Authentication failure: ${responseBody.Authentication}`,
                                });
                                return authFailedResponse;
                            }
                            // NOOP
                        }
                    },
                    ...((!args || !args.disableInterceptors) ? evaServiceInterceptors.getResponseInterceptors() : []),
                ],
            },
        };
    }
    /**
     * Sets the request payload
     *
     * @param {SVC['request']} request
     */
    setRequest(request) {
        this.data.request = request;
    }
    /**
     * Convenience method to create a Fetch API request for manually calling a
     * service. This can be useful for SSR implementations.
     *
     * NOTE: Manual fetch calls will not log EVA warnings or check for content errors
     *
     * See: https://springtreesolutions.github.io/eva-sdk-repo/#/services/endpoint?id=eva-warning
     * See: https://springtreesolutions.github.io/eva-sdk-repo/#/services/endpoint?id=eva-response-error
     *
     * @param {IEvaServiceCallOptions} [options]
     */
    buildFetchRequest(options) {
        // Prepare the call configuration and determine the service url
        //
        const { requestUrl, config } = this.prepareCallOptions(options);
        // Re-apply certain core/service setting in-case they have been updated
        //
        const coreSettings = getCoreSettings();
        const serviceSettings = getServiceSettings();
        this.defaultOptions.timeout = serviceSettings.defaultTimeout;
        this.defaultOptions.headers['Accept-Language'] = options?.language || coreSettings.language;
        // Merge the default options. The default ky instance does this for us
        //
        const mergedOptions = {
            ...this.defaultOptions,
            ...config,
        };
        // Build the headers with the correct typing and Headers object
        //
        const headers = new Headers();
        for (const [key, value] of Object.entries(this.defaultOptions.headers)) {
            // Set default if not specified in config
            //
            if (!config.headers || !config.headers[key]) {
                headers.append(key, value);
            }
        }
        for (const [key, value] of Object.entries(config.headers)) {
            headers.append(key, value);
        }
        const fetchRequest = new Request(requestUrl, {
            headers,
            body: mergedOptions.json ? JSON.stringify(config.json) : undefined,
            cache: mergedOptions.cache,
            credentials: mergedOptions.credentials,
            integrity: mergedOptions.integrity,
            keepalive: mergedOptions.keepalive,
            method: mergedOptions.method,
            mode: mergedOptions.mode,
            redirect: mergedOptions.redirect,
            referrer: mergedOptions.referrer,
            referrerPolicy: mergedOptions.referrerPolicy,
            window: mergedOptions.window,
        });
        return fetchRequest;
    }
    /**
     * Call the EVA backend service with the current value of `this.data.request`
     * as the payload. The response will be set on `this.data.response` and the
     * full response will be available on `this.fullResponse`.
     * Unless otherwise configured in the constructor all active interceptors will
     * be used on the request and response as required.
     *
     * @returns {Promise<RES>}
     */
    async call(options) {
        // Check if this service instance has already been used
        //
        if (this.abortController !== undefined) {
            throw new Error('EVA Service instance has already been used. Please create an instance per call');
        }
        // Prepare the call configuration and determine the service url
        //
        const { requestUrl, config } = this.prepareCallOptions(options);
        // Add the abort controller signal if supplied or create an abort controller
        //
        config.signal = options?.abortSignal;
        if (!config.signal) {
            try {
                this.abortController = new AbortController();
                config.signal = this.abortController.signal;
            }
            catch (error) {
                // No Abort controller available
            }
        }
        logger.debug(`[EVA:${this.serviceName}] Doing call ${requestUrl}`, config)();
        try {
            // Provide the signal for the abort controller to function
            //
            this.fetchResponse = await ky(requestUrl, config);
            const responseBody = await safeGetResponseJSON(this.fetchResponse);
            logger.debug(`[EVA:${this.serviceName}] Call response status`, this.fetchResponse.status)();
            logger.trace(`[EVA:${this.serviceName}] Call response headers`, JSON.stringify(this.fetchResponse.headers))();
            logger.trace(`[EVA:${this.serviceName}] Call response data`, responseBody)();
            // Store response payload
            //
            this.data.response = responseBody;
            // Resolve with typed response object
            //
            return this.data;
        }
        catch (error) {
            logger.error(`[EVA:${this.serviceName}] Call failed`, error)();
            throw error;
        }
    }
    /**
     * Helper method to prepare the Fetch options for a service call
     */
    prepareCallOptions(options) {
        // Collect required settings
        //
        const serviceSettings = getServiceSettings();
        const applicationTokens = serviceSettings.applicationTokens || {};
        // Build the request url by combining the service path with the endpoint
        //
        const requestUrl = `${this.endpoint.endPointUri}${this.data.path}`;
        const requestOrigin = new URL(this.endpoint.endPointUri).origin;
        // Merge any provided headers with the default headers to build the final config
        //
        const headers = {
            ...this.defaultOptions.headers,
            ...options?.headers,
        };
        const config = {
            ...this.defaultOptions,
            headers,
            json: this.data.request,
        };
        // Override timeout if requested
        // Don't use falsy check to allow 0 values
        //
        if (options?.timeout !== undefined) {
            config.timeout = options.timeout;
        }
        // Add authentication token if supplied
        // We will check if this is an EVA JWT token
        // The non-JWT token length is 64 while the JWT tokens are longer
        // The JWT tokens need to be prefixed with `eva`
        //
        const authenticationToken = options?.authenticationToken;
        if (authenticationToken) {
            headers.Authorization = authenticationToken.length > 64 && authenticationToken.substring(0, 4) !== 'eva ' ? `eva ${authenticationToken}` : authenticationToken;
        }
        // Override default language if supplied
        //
        if (options?.language) {
            headers['Accept-Language'] = options.language;
        }
        // Add elevation token if supplied
        //
        if (options?.elevationToken) {
            headers['EVA-Elevation-Token'] = options.elevationToken;
        }
        // Add a unique call identifier header which will be echoed by the EVA backend
        //
        headers['EVA-App-ContextID'] = (options && options.serviceContext) || v4();
        headers['EVA-Service-Name'] = this.data.name;
        headers['EVA-App-PayloadID'] = safeStableJSONHash(this.data.request);
        // Add chain context if provided
        //
        if (options?.chainContext) {
            headers['EVA-App-ContextChainID'] = options.chainContext;
        }
        // Add the application instance identifier if provided
        //
        if (serviceSettings.applicationInstanceId) {
            headers['EVA-App-InstanceId'] = serviceSettings.applicationInstanceId;
        }
        // Add the application token if available
        //
        if (options?.applicationToken) {
            headers['EVA-App-Token'] = options.applicationToken;
        }
        else if (applicationTokens[requestOrigin]) {
            headers['EVA-App-Token'] = applicationTokens[requestOrigin];
        }
        // Add the currently used station ID if set
        //
        if (options?.currentStationID) {
            headers['EVA-StationID'] = `${options.currentStationID}`;
        }
        else if (serviceSettings.currentStationID) {
            headers['EVA-StationID'] = `${serviceSettings.currentStationID}`;
        }
        // Add the requested organization unit id
        //
        if (options?.requestedOrganizationUnitID) {
            headers['EVA-Requested-OrganizationUnitID'] = `${options.requestedOrganizationUnitID}`;
        }
        else if (serviceSettings.requestedOrganizationUnitID) {
            headers['EVA-Requested-OrganizationUnitID'] = `${serviceSettings.requestedOrganizationUnitID}`;
        }
        // Add the requested organization unit query
        //
        if (options?.requestedOrganizationUnitQuery) {
            headers['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(options.requestedOrganizationUnitQuery);
        }
        else if (serviceSettings.requestedOrganizationUnitQuery) {
            headers['EVA-Requested-OrganizationUnit-Query'] = JSON.stringify(serviceSettings.requestedOrganizationUnitQuery);
        }
        // Add the id mode option if set
        //
        if (options?.backendIdMode) {
            headers['EVA-IDs-Mode'] = `${options.backendIdMode}`;
        }
        else if (serviceSettings.backendIdMode) {
            headers['EVA-IDs-Mode'] = `${serviceSettings.backendIdMode}`;
        }
        // Add the id backend id option if set
        //
        if (options?.backendIdSystemID) {
            headers['EVA-IDs-BackendSystemID'] = `${options.backendIdSystemID}`;
        }
        else if (serviceSettings.backendIdSystemID) {
            headers['EVA-IDs-BackendSystemID'] = `${serviceSettings.backendIdSystemID}`;
        }
        return { config, requestUrl };
    }
}
//# sourceMappingURL=service.js.map